import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@offline-mui/material'
import { alpha } from '@offline-mui/material/styles'
import { useMemo } from 'react'
import type { ReactNode } from 'react'
import type { DistributionPayload } from '../api/inventory'
import { DateField } from './ui/DateField'

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type DistributionChannelDraft = {
  enabled: boolean
  include: string
  exclude: string
}

export type DistributionDraft = {
  gigi: DistributionChannelDraft
  api: DistributionChannelDraft
}

export const EMPTY_DISTRIBUTION: DistributionDraft = {
  gigi: { enabled: false, include: '', exclude: '' },
  api: { enabled: false, include: '', exclude: '' },
}

export type BlockDetailsValues = {
  platingCarrier: string
  blockSource: string
  totalSeatsBlocked: string
  totalSeatsBooked: string
  /** YYYY-MM-DD */
  ticketingDate: string
  /** YYYY-MM-DD */
  seatReleaseDate: string
  multiplePnrs: string
  distribution: DistributionDraft
}

type BlockDetailsSectionProps = {
  values: BlockDetailsValues
  onChange: <K extends keyof BlockDetailsValues>(key: K, value: BlockDetailsValues[K]) => void
}

const CHANNELS: Array<{ key: keyof DistributionDraft; label: string; hint: string }> = [
  { key: 'gigi', label: 'GIGI', hint: 'Agent booking platform' },
  { key: 'api', label: 'API', hint: 'Partner integrations' },
]

/** True when both counts are numbers and more seats are booked than blocked. */
export function bookedExceedsBlocked(blocked: string, booked: string): boolean {
  const blockedNum = Number(blocked)
  const bookedNum = Number(booked)
  return (
    blocked.trim() !== '' &&
    booked.trim() !== '' &&
    Number.isFinite(blockedNum) &&
    Number.isFinite(bookedNum) &&
    bookedNum > blockedNum
  )
}

/** Comma-separated form value → API list payload (null when empty). */
function listPayload(value: string): string | null {
  return value.trim() || null
}

export function distributionToPayload(draft: DistributionDraft): DistributionPayload {
  return {
    distributeGigi: draft.gigi.enabled,
    gigiAgencyInclude: draft.gigi.enabled ? listPayload(draft.gigi.include) : null,
    gigiAgencyExclude: draft.gigi.enabled ? listPayload(draft.gigi.exclude) : null,
    distributeApi: draft.api.enabled,
    apiAgencyInclude: draft.api.enabled ? listPayload(draft.api.include) : null,
    apiAgencyExclude: draft.api.enabled ? listPayload(draft.api.exclude) : null,
  }
}

function GroupLabel({ children, action }: { children: string; action?: ReactNode }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
    >
      <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>
        {children}
      </Typography>
      {action}
    </Stack>
  )
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function BlockDetailsSection({ values, onChange }: BlockDetailsSectionProps) {
  const pnrList = useMemo(
    () =>
      values.multiplePnrs
        .split(/[,\n]/)
        .map((item) => item.trim())
        .filter(Boolean),
    [values.multiplePnrs],
  )

  const seatsOverbooked = bookedExceedsBlocked(values.totalSeatsBlocked, values.totalSeatsBooked)
  const seatsRemaining =
    !seatsOverbooked &&
    values.totalSeatsBlocked !== '' &&
    values.totalSeatsBooked !== '' &&
    Number.isFinite(Number(values.totalSeatsBlocked)) &&
    Number.isFinite(Number(values.totalSeatsBooked))
      ? Math.max(0, Number(values.totalSeatsBlocked) - Number(values.totalSeatsBooked))
      : null

  const updateChannel = (
    key: keyof DistributionDraft,
    patch: Partial<DistributionChannelDraft>,
  ) => {
    onChange('distribution', {
      ...values.distribution,
      [key]: { ...values.distribution[key], ...patch },
    })
  }

  /** Empty or invalid seat counts fall back to zero once the field loses focus. */
  const settleSeats = (key: 'totalSeatsBlocked') => {
    const value = values[key].trim()
    if (value === '' || !Number.isFinite(Number(value)) || Number(value) < 0) {
      onChange(key, '0')
    }
  }

  const removePnr = (pnr: string) => {
    onChange('multiplePnrs', pnrList.filter((item) => item !== pnr).join(', '))
  }

  return (
    <Stack spacing={3}>
      {/* Line 1 ----------------------------------------------------- */}
      <Box>
        <GroupLabel
          action={
            seatsRemaining != null ? (
              <Chip
                size="small"
                color={seatsRemaining > 0 ? 'success' : 'default'}
                label={`${seatsRemaining} seats remaining`}
              />
            ) : undefined
          }
        >
          Carrier & seats
        </GroupLabel>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              required
              label="Validating carrier"
              value={values.platingCarrier}
              onChange={(event) => onChange('platingCarrier', event.target.value.toUpperCase())}
              placeholder="AA"
              slotProps={{ htmlInput: { maxLength: 3 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Booking source"
              value={values.blockSource}
              onChange={(event) => onChange('blockSource', event.target.value)}
              placeholder="Airline, consolidator, internal…"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              type="number"
              label="Blocked seats"
              value={values.totalSeatsBlocked}
              onChange={(event) => onChange('totalSeatsBlocked', event.target.value)}
              onBlur={() => settleSeats('totalSeatsBlocked')}
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              type="number"
              label="Booked seats"
              value={values.totalSeatsBooked.trim() === '' ? '0' : values.totalSeatsBooked}
              error={seatsOverbooked}
              helperText={
                seatsOverbooked ? 'Exceeds blocked seats' : 'Updated from bookings'
              }
              slotProps={{
                input: { readOnly: true },
                htmlInput: { tabIndex: -1, 'aria-readonly': true },
              }}
              sx={{
                '& .MuiInputBase-root': { bgcolor: 'grey.50' },
                '& .MuiInputBase-input': { color: 'text.secondary', cursor: 'default' },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Line 2 ----------------------------------------------------- */}
      <Box>
        <GroupLabel>Dates & PNRs</GroupLabel>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DateField
              required
              label="Ticketing deadline"
              value={values.ticketingDate}
              onChange={(value) => onChange('ticketingDate', value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <DateField
              label="Seat release date"
              value={values.seatReleaseDate}
              onChange={(value) => onChange('seatReleaseDate', value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Airline PNRs"
              value={values.multiplePnrs}
              onChange={(event) => onChange('multiplePnrs', event.target.value)}
              placeholder="ABC123, DEF456"
              helperText="Separate multiple PNRs with commas"
            />
            {pnrList.length > 0 ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>
                {pnrList.map((pnr) => (
                  <Chip
                    key={pnr}
                    label={pnr}
                    onDelete={() => removePnr(pnr)}
                    variant="outlined"
                    size="small"
                    sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                  />
                ))}
              </Stack>
            ) : null}
          </Grid>
        </Grid>
      </Box>

      {/* Line 3 ----------------------------------------------------- */}
      <Box>
        <GroupLabel>Distribute to</GroupLabel>
        <Stack spacing={1.5}>
          {CHANNELS.map((channel) => {
            const draft = values.distribution[channel.key]
            return (
              <Box
                key={channel.key}
                sx={{
                  display: 'grid',
                  gap: 2,
                  alignItems: 'start',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 220px) 1fr 1fr' },
                  p: 1.5,
                  pl: 2,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: draft.enabled ? 'primary.main' : 'divider',
                  bgcolor: (t) =>
                    draft.enabled ? alpha(t.palette.primary.main, 0.04) : 'background.paper',
                  transition: 'border-color .15s ease, background-color .15s ease',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={draft.enabled}
                      onChange={(event) =>
                        updateChannel(channel.key, { enabled: event.target.checked })
                      }
                      slotProps={{ input: { 'aria-label': `Distribute to ${channel.label}` } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {channel.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {channel.hint}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'center', minHeight: 40 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={`${channel.label} include agencies`}
                  value={draft.include}
                  disabled={!draft.enabled}
                  onChange={(event) => updateChannel(channel.key, { include: event.target.value })}
                  placeholder="AG1, AG2"
                  helperText="Blank means all agencies"
                  slotProps={{ formHelperText: { sx: { whiteSpace: 'nowrap' } } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={`${channel.label} exclude agencies`}
                  value={draft.exclude}
                  disabled={!draft.enabled}
                  onChange={(event) => updateChannel(channel.key, { exclude: event.target.value })}
                  placeholder="AG9, AG10"
                  helperText="Comma-separated"
                  slotProps={{ formHelperText: { sx: { whiteSpace: 'nowrap' } } }}
                />
              </Box>
            )
          })}
        </Stack>
      </Box>
    </Stack>
  )
}
